import { useEffect, useState } from 'react';

import { hasValue } from 'utils/hasValue';

import { useGetCryptoExchangeRates } from 'api/customerApi';
import type {
  GetCryptoExchangeRates422,
  GetCryptoExchangeRatesQueryResult
} from 'api/customerApi';

export const RATE_MAX_REFRESH_SECONDS = 15;
const RATE_MIN_REFRESH_SECONDS = 3;
const SECOND_MS = 1000;

export const useCryptoExchangeRate = ({
  organisationId,
  pair,
  amount,
  virtualAccountNumber,
  enabled
}: {
  organisationId: number;
  pair: string | undefined;
  amount: string;
  virtualAccountNumber: string | undefined;
  enabled: boolean;
}) => {
  const [debouncedAmount, setDebouncedAmount] = useState('');
  const [secondsUntilExpiry, setSecondsUntilExpiry] = useState<number | null>(
    null
  );

  // Debounce amount by 500ms; clear immediately when empty.
  useEffect(() => {
    if (amount === '') {
      setDebouncedAmount('');
      return;
    }
    const id = setTimeout(() => setDebouncedAmount(amount), 500);
    return () => clearTimeout(id);
  }, [amount]);

  const exchangeRateParams = hasValue(pair)
    ? {
        pair,
        amount: debouncedAmount,
        virtual_account_number: virtualAccountNumber
      }
    : undefined;

  const isRateEnabled =
    enabled && debouncedAmount !== '' && hasValue(exchangeRateParams);

  const {
    data: rateData,
    isFetching: isRateLoading,
    isError: isRateError,
    error: rateError
  } = useGetCryptoExchangeRates<
    GetCryptoExchangeRatesQueryResult,
    // 401s never surface here: the auth interceptor resolves them with a token refresh.
    GetCryptoExchangeRates422
  >(organisationId, exchangeRateParams ?? { pair: '', amount: '' }, {
    query: {
      enabled: isRateEnabled,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: (query) => {
        const refreshAt = query.state.data?.refresh_at;
        if (!hasValue(refreshAt)) return RATE_MAX_REFRESH_SECONDS * SECOND_MS;

        return Math.max(
          refreshAt * SECOND_MS - Date.now(),
          RATE_MIN_REFRESH_SECONDS * SECOND_MS
        );
      }
    }
  });

  const expiryAt = rateData?.expiry_at;
  const fetchedAt = rateData?.fetched_at;

  const validitySeconds =
    hasValue(expiryAt) && hasValue(fetchedAt) && expiryAt > fetchedAt
      ? expiryAt - fetchedAt
      : RATE_MAX_REFRESH_SECONDS;

  useEffect(() => {
    if (!isRateEnabled || !hasValue(expiryAt)) {
      setSecondsUntilExpiry(null);
      return;
    }

    const updateSecondsUntilExpiry = () => {
      setSecondsUntilExpiry(
        Math.max(Math.ceil(expiryAt - Date.now() / SECOND_MS), 0)
      );
    };

    updateSecondsUntilExpiry();
    const id = setInterval(updateSecondsUntilExpiry, SECOND_MS);

    return () => clearInterval(id);
  }, [isRateEnabled, expiryAt]);

  const isRateExpired = hasValue(expiryAt) && secondsUntilExpiry === 0;

  return {
    rateData,
    isRateLoading,
    isRateError,
    rateError,
    secondsUntilExpiry,
    validitySeconds,
    isRateExpired
  };
};
